/**
 * Schlanker Cognito-Client für die öffentlichen (nicht signierten) User-Pool-Aufrufe.
 *
 * Anmeldung ausschließlich mit `USER_PASSWORD_AUTH`: Nur dieser Fluss übergibt das
 * Passwort an den Migrationstrigger (SRP würde die stille Übernahme verhindern).
 * Protokoll laut Cognito API Reference: POST auf den regionalen Endpunkt,
 * `X-Amz-Target: AWSCognitoIdentityProviderService.<Aktion>`, JSON 1.1.
 */
export type AuthErrorCode =
  | 'invalid-credentials'
  | 'reset-required'
  | 'not-confirmed'
  | 'invalid-code'
  | 'weak-password'
  | 'rate-limited'
  | 'unsupported-challenge'
  | 'network'
  | 'unknown';

export class AuthError extends Error {
  constructor(
    readonly code: AuthErrorCode,
    readonly detail?: string,
  ) {
    super(code);
    this.name = 'AuthError';
  }
}

export interface Tokens {
  accessToken: string;
  idToken: string;
  /** Fehlt bei der Erneuerung; der alte bleibt gültig. */
  refreshToken?: string;
  /** Ablauf des Access-Tokens in ms seit Epoche. */
  expiresAt: number;
}

const ERROR_MAP: Record<string, AuthErrorCode> = {
  NotAuthorizedException: 'invalid-credentials',
  UserNotFoundException: 'invalid-credentials',
  // Der Migrationstrigger lehnt mit leerer Antwort ab (dann NotAuthorizedException);
  // ein unerwarteter Triggerfehler soll trotzdem nichts verraten.
  UserLambdaValidationException: 'invalid-credentials',
  PasswordResetRequiredException: 'reset-required',
  UserNotConfirmedException: 'not-confirmed',
  CodeMismatchException: 'invalid-code',
  ExpiredCodeException: 'invalid-code',
  InvalidPasswordException: 'weak-password',
  LimitExceededException: 'rate-limited',
  TooManyRequestsException: 'rate-limited',
  TooManyFailedAttemptsException: 'rate-limited',
};

export interface CognitoClientOptions {
  region: string;
  clientId: string;
  fetch?: typeof fetch;
  now?: () => number;
}

export function createCognitoClient(options: CognitoClientOptions) {
  const endpoint = `https://cognito-idp.${options.region}.amazonaws.com/`;
  const doFetch = options.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  const now = options.now ?? (() => Date.now());

  async function call<T>(action: string, body: Record<string, unknown>): Promise<T> {
    let res: Response;
    try {
      res = await doFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': `AWSCognitoIdentityProviderService.${action}`,
        },
        body: JSON.stringify(body),
      });
    } catch {
      throw new AuthError('network');
    }
    const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    if (!res.ok) {
      const type = String(json['__type'] ?? '').split('#').pop() ?? '';
      const message = typeof json['message'] === 'string' ? json['message'] : undefined;
      // Fehler im Post-Authentication-Trigger sind vorübergehend, keine falschen Zugangsdaten.
      if (type === 'UserLambdaValidationException' && message?.startsWith('PostAuthentication')) throw new AuthError('unknown', message);
      throw new AuthError(ERROR_MAP[type] ?? 'unknown', message);
    }
    return json as T;
  }

  function toTokens(result: Record<string, unknown> | undefined): Tokens {
    const accessToken = result?.['AccessToken'];
    const idToken = result?.['IdToken'];
    const expiresIn = Number(result?.['ExpiresIn'] ?? 0);
    if (typeof accessToken !== 'string' || typeof idToken !== 'string' || !(expiresIn > 0)) throw new AuthError('unknown');
    const refreshToken = result?.['RefreshToken'];
    return { accessToken, idToken, expiresAt: now() + expiresIn * 1000, ...(typeof refreshToken === 'string' ? { refreshToken } : {}) };
  }

  return {
    async signIn(email: string, password: string): Promise<Tokens> {
      const res = await call<{ AuthenticationResult?: Record<string, unknown>; ChallengeName?: string }>('InitiateAuth', {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: options.clientId,
        AuthParameters: { USERNAME: email.trim(), PASSWORD: password },
      });
      if (res.ChallengeName) throw new AuthError('unsupported-challenge', res.ChallengeName);
      return toTokens(res.AuthenticationResult);
    },
    async refresh(refreshToken: string): Promise<Tokens> {
      const res = await call<{ AuthenticationResult?: Record<string, unknown> }>('InitiateAuth', {
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: options.clientId,
        AuthParameters: { REFRESH_TOKEN: refreshToken },
      });
      return toTokens(res.AuthenticationResult);
    },
    async forgotPassword(email: string): Promise<void> {
      await call('ForgotPassword', { ClientId: options.clientId, Username: email.trim() });
    },
    async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
      await call('ConfirmForgotPassword', {
        ClientId: options.clientId,
        Username: email.trim(),
        ConfirmationCode: code.trim(),
        Password: newPassword,
      });
    },
    async revoke(refreshToken: string): Promise<void> {
      await call('RevokeToken', { ClientId: options.clientId, Token: refreshToken });
    },
  };
}

export type CognitoClient = ReturnType<typeof createCognitoClient>;

/** Liest die Nutzlast eines JWT ohne Prüfung – nur für Anzeige (E-Mail, Sprache). */
export function decodeJwtPayload(token: string): Record<string, unknown> {
  const part = token.split('.')[1];
  if (!part) return {};
  try {
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      Array.from(atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')))
        .map(ch => `%${ch.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join(''),
    );
    const payload: unknown = JSON.parse(json);
    return typeof payload === 'object' && payload !== null ? (payload as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}
